import { Component } from "react";

import { currentOrganizationVar } from "@cache/cache";
import { classNames } from "@helpers/css";

import { SkillItem } from "./skill-commands-extension";

type Props = {
  items: SkillItem[];
  command: (props: SkillItem) => void;
};

type State = {
  selectedIndex: number;
};

const isPersonal = (item: SkillItem) => item.visibility === "PERSONAL";

export default class SkillCommandsList extends Component<Props, State> {
  state: State = {
    selectedIndex: 0,
  };

  /**
   * Items in render order: your (personal) skills first, then the org's skills
   * (workspace + platform). `selectedIndex` indexes into THIS list — not
   * `this.props.items`, which may arrive in any order from the server.
   */
  getDisplayItems(): SkillItem[] {
    const personal = this.props.items.filter(isPersonal);
    const org = this.props.items.filter((item) => !isPersonal(item));
    return [...personal, ...org];
  }

  componentDidUpdate(oldProps: Props) {
    if (this.props.items !== oldProps.items) {
      this.setState({ selectedIndex: 0 });
    }
  }

  onKeyDown({ event }: { event: KeyboardEvent }) {
    if (event.key === "ArrowUp") {
      this.upHandler();
      return true;
    }
    if (event.key === "ArrowDown") {
      this.downHandler();
      return true;
    }
    if (event.key === "Enter") {
      if (this.props.items.length > 0) {
        this.enterHandler();
        return true;
      }
      return false;
    }
    if (event.key === "Tab") {
      if (this.props.items.length > 0) {
        this.enterHandler();
        return true;
      }
      return false;
    }
    return false;
  }

  upHandler() {
    const newIndex = this.state.selectedIndex - 1;
    if (newIndex >= 0) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  downHandler() {
    const newIndex = this.state.selectedIndex + 1;
    if (newIndex < this.getDisplayItems().length) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  enterHandler() {
    this.selectItem(this.state.selectedIndex);
  }

  selectItem(index: number) {
    const item = this.getDisplayItems()[index];
    if (item) {
      this.props.command(item);
    }
  }

  renderSection(label: string, sectionItems: SkillItem[]) {
    if (sectionItems.length === 0) return null;
    const displayItems = this.getDisplayItems();
    return (
      <div key={label}>
        <div className="sticky top-0 bg-gray-50 px-3 py-1 text-[11px] font-semibold text-gray-500 border-b">
          {label}
        </div>
        {sectionItems.map((item) => {
          const flatIndex = displayItems.indexOf(item);
          return (
            <button
              type="button"
              role="option"
              aria-selected={flatIndex === this.state.selectedIndex}
              className={classNames(
                "block w-80 text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50",
                flatIndex === this.state.selectedIndex && "bg-gray-100",
              )}
              key={item.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.selectItem(flatIndex);
              }}
            >
              <span className="block font-mono text-xs font-medium text-blue-link truncate">
                <span aria-hidden="true">/</span>
                <span>{item.slug}</span>
              </span>
              {item.description && (
                <span className="block text-gray-400 text-xs mt-0.5">
                  {item.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  render() {
    const { items } = this.props;
    if (items.length === 0) return null;

    const personal = items.filter(isPersonal);
    const org = items.filter((item) => !isPersonal(item));
    const orgName = currentOrganizationVar()?.name || "Organization";

    return (
      <div
        className="relative rounded bg-white border text-sm shadow overflow-y-auto max-h-72"
        aria-label="ask-ai-skill-commands"
        role="listbox"
      >
        {this.renderSection("Your skills", personal)}
        {this.renderSection(orgName, org)}
      </div>
    );
  }
}
